import { type ReactNode, useCallback, useMemo } from "react";

import { useErrorTracker } from "@dfp/react-app-insights";
import { useSnackbar } from "@dfp/react-components";
import { UserCancelledRequestError } from "@dfp/react-legacy-async-components";

import { useTranslateGenericError } from "../errors/useTranslateGenericError.tsx";
import { ErrorHandlerContext } from "./ErrorHandlerContext.ts";
import type { ErrorHandlerContextType, HandleErrorOptions } from "./types.ts";

type Props = {
  children: ReactNode;
};

export function ErrorHandlerProvider({ children }: Props) {
  const { showSnackbar } = useSnackbar();
  const translate = useTranslateGenericError();
  const trackError = useErrorTracker();

  const handleError = useCallback(
    (error: unknown, options: HandleErrorOptions = {}) => {
      const err = error instanceof Error ? error : new Error(String(error));

      if (err instanceof UserCancelledRequestError) {
        return;
      }

      if (!options.skipTracking) {
        trackError(err, options.context);
      }

      if (options.silent) {
        return;
      }

      const defaultMessage = translate(err);
      let resolved: string | undefined;

      if (typeof options.message === "function") {
        resolved = options.message(err, defaultMessage);
      } else if (typeof options.message === "string") {
        resolved = options.message;
      }

      showSnackbar({
        message: resolved ?? defaultMessage,
        severity: options.severity ?? "error",
      });
    },
    [showSnackbar, translate, trackError],
  );

  const value = useMemo<ErrorHandlerContextType>(
    () => ({ handleError }),
    [handleError],
  );

  return (
    <ErrorHandlerContext.Provider value={value}>
      {children}
    </ErrorHandlerContext.Provider>
  );
}
